import { GoalType } from '@griply/types';
import { ID } from 'shared/types/id';

import { GoalTreeItem, GoalTreeItemIds } from './types';

export type GoalTreeMenuSelection = {
  goalId?: ID;
  lifeAreaId?: ID;
  /** Highlights the "No life area" bucket — only meaningful when it is shown. */
  noLifeArea?: boolean;
};

export type GoalTreeMultiSelection = {
  goalIds: ID[];
  lifeAreaIds: ID[];
};

export type ResolveGoalTreeItemStateOptions = {
  item: GoalTreeItem;
  searchValue: string;
  selection?: GoalTreeMenuSelection;
  // Present = multi-select; `selection` is then ignored.
  multiSelection?: GoalTreeMultiSelection;
  disabledGoalIds: Set<ID>;
  disableProjects?: boolean;
};

export type GoalTreeItemState = {
  isSelected: boolean;
  isDisabled: boolean;
  isParent: boolean;
};

const isDisabledGoal = (
  item: GoalTreeItem,
  disabledGoalIds: Set<ID>,
  disableProjects?: boolean,
): boolean =>
  item.type === GoalTreeItemIds.Goal &&
  (disabledGoalIds.has(item.id) ||
    (!!disableProjects && item.goalType === GoalType.Project));

const resolveMultiSelectState = (
  item: GoalTreeItem,
  multiSelection: GoalTreeMultiSelection,
  disabledGoalIds: Set<ID>,
  disableProjects?: boolean,
): GoalTreeItemState => {
  if (item.type === GoalTreeItemIds.NoLifeArea) {
    return { isSelected: false, isDisabled: false, isParent: false };
  }

  if (item.type === GoalTreeItemIds.LifeArea) {
    const isSelected = multiSelection.lifeAreaIds.includes(item.id);
    return { isSelected, isDisabled: false, isParent: isSelected };
  }

  const isSelected = multiSelection.goalIds.includes(item.id);
  return {
    isSelected,
    isDisabled: isDisabledGoal(item, disabledGoalIds, disableProjects),
    isParent: isSelected,
  };
};

export const resolveGoalTreeItemState = ({
  item,
  searchValue,
  selection,
  multiSelection,
  disabledGoalIds,
  disableProjects,
}: ResolveGoalTreeItemStateOptions): GoalTreeItemState => {
  if (multiSelection) {
    return resolveMultiSelectState(
      item,
      multiSelection,
      disabledGoalIds,
      disableProjects,
    );
  }

  // Selecting the bucket the caller already sits in is a no-op, so it stays disabled.
  if (item.type === GoalTreeItemIds.NoLifeArea) {
    const isSelected = !!selection?.noLifeArea && !searchValue;
    return { isSelected, isDisabled: isSelected, isParent: false };
  }

  if (item.type === GoalTreeItemIds.LifeArea) {
    const isSelected = selection?.lifeAreaId === item.id && !searchValue;
    return { isSelected, isDisabled: isSelected, isParent: false };
  }

  // Descendants of the picked goal stay valid targets unless the caller disabled them.
  const isParent = selection?.goalId === item.id;
  return {
    isSelected: isParent,
    isDisabled: isDisabledGoal(item, disabledGoalIds, disableProjects),
    isParent,
  };
};
